import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { getCookie, resetTokensAndSendToLogin, setCookie } from 'helpers/cookies';
import blobErrorToObject from 'helpers/blobErrorToObject';
import users from './users';
import { functionCollectBrowserData } from 'helpers/browserData/functionCollectBrowserData';
import { refresTokensHandle } from 'helpers/tokens';

const correctHeaders = window.location?.href?.includes('localhost:3000')
  ? { "x-forwarded-for": "0.0.0.0", "Access-Control-Allow-Origin": "*"}
  : { "Access-Control-Allow-Origin": "*" };

const analyticsTemplate = axios.create({
  baseURL: window.REACT_APP_ANALYTICS_URL !== 'REPLACE_REACT_APP_ANALYTICS_URL' ? window.REACT_APP_ANALYTICS_URL : process.env.REACT_APP_ANALYTICS_URL || 'http://localhost:8088/api',
  // withCredentials: true,
  headers: correctHeaders,
});

analyticsTemplate.interceptors.request.use(async(config: InternalAxiosRequestConfig) => {
  let accessToken = getCookie('athena-token');
  let decodeInfo: any;
  if (accessToken) {
    try {
      decodeInfo = jwtDecode(accessToken);
    } catch (error) {
      Promise.reject('Error decoding token');
    }
    const expTime = decodeInfo?.exp && decodeInfo.exp * 1000;
    const curTime = new Date().getTime();

    if (expTime && expTime - curTime <= 50000) {
      const refresh_token = getCookie('refresh_token');      
      if (refresh_token) {
        try {
          const newTokens = await refresTokensHandle(refresh_token);
          accessToken = newTokens.access_token;
        } catch (error) {
          Promise.reject('Error refreshing tokens');
          resetTokensAndSendToLogin();
        };
      };
    };

    config.headers!.Authorization = `Bearer ${accessToken}`
  } else {
    setCookie('athena-token', '', 1);
  }
  return config;
});

let cntTryingUpdateFingerprint = 0;

analyticsTemplate.interceptors.response.use(( response: AxiosResponse ) => {
  return response
}, async (error: AxiosError<any>) => {
  if (error.response?.status === 401 && cntTryingUpdateFingerprint < 1) {
    cntTryingUpdateFingerprint++;

    try {
      const formData = new FormData();
      const browserData = await functionCollectBrowserData();
      formData.append('browser_data', JSON.stringify(browserData) || '');

      const freshTokens = await users.updateFingerprint(formData);
      setCookie('athena-token', freshTokens.data.access_token, 1);
      setCookie('refresh_token', freshTokens.data.refresh_token, 1);

      return analyticsTemplate.request(error.request.responseURL);
    } catch {
      resetTokensAndSendToLogin();
    }
  } else if (cntTryingUpdateFingerprint >= 1) {
    resetTokensAndSendToLogin();
  };

  if (error?.response?.data.constructor.name === 'Blob'){
    error.response.data = await blobErrorToObject(error?.response?.data)
  };
  return Promise.reject(error)
});

const getAnalyticsMainInfo = (payload: { room?: string, from?: string, to?: string }) =>
  analyticsTemplate.get(`/v1/analytics/main-info/`, {params: payload});

const getAnalyticsInvites = (payload: { room?: string, from?: string, to?: string }) =>
  analyticsTemplate.get(`/v1/analytics/invites/`, {params: payload});

const getAnalyticsTempsLinks = (payload: { room?: string, from?: string, to?: string }) =>
  analyticsTemplate.get(`/v1/analytics/temp-links/`, {params: payload});

const getAnalyticsAgreements = (payload: { room?: string, from?: string, to?: string }) =>
  analyticsTemplate.get(`/v1/analytics/agreements/`, {params: payload});

const getAnalyticsSignatures = (payload: { room?: string, from?: string, to?: string }) =>
  analyticsTemplate.get(`/v1/analytics/signatures/`, {params: payload});

const getAnalyticsDocumentsActivity = (payload: { room?: string, from?: string, to?: string }) =>
  analyticsTemplate.get(`/v1/analytics/documents/activity/`, {params: payload});

const getAnalyticsUsersActivity = (payload: { room?: string, from?: string, to?: string }) =>
  analyticsTemplate.get(`/v1/analytics/users/activity/`, {params: payload});

const analytics = {
  getAnalyticsMainInfo,
  getAnalyticsInvites,
  getAnalyticsTempsLinks,
  getAnalyticsAgreements,
  getAnalyticsSignatures,
  getAnalyticsDocumentsActivity,
  getAnalyticsUsersActivity,
};

export default analytics;
